import React from "react";
import { Modal, DatePicker, TimePicker, Input, Button, ConfigProvider, theme as antTheme } from "antd";
import { Clock } from "lucide-react";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { apiInstance as api } from "../../../hooks/useApi";
import { useDashTheme } from "../hooks/useDashTheme";
import { useTheme } from "../../../contexts/ThemeContext";

function fmtMs(ms) {
  if (!ms || ms <= 0) return null;
  const min = Math.floor(ms / 60000);
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

function todayDayjs() {
  return dayjs();
}

export function IdleRequestModal({ isOpen, onClose }) {
  const t = useDashTheme();
  const { mode } = useTheme();
  const isDark = mode === "dark";

  const [date, setDate] = React.useState(null);
  const [fromTime, setFromTime] = React.useState(null);
  const [toTime, setToTime] = React.useState(null);
  const [reason, setReason] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [errors, setErrors] = React.useState({});
  const [submitted, setSubmitted] = React.useState(false);

  // Reset on open
  React.useEffect(() => {
    if (isOpen) {
      setDate(todayDayjs());
      setFromTime(null);
      setToTime(null);
      setReason("");
      setErrors({});
      setSubmitted(false);
    }
  }, [isOpen]);

  // Compute duration
  const startDt = date && fromTime
    ? date.clone().hour(fromTime.hour()).minute(fromTime.minute()).second(0).millisecond(0)
    : null;
  const endDt = date && toTime
    ? date.clone().hour(toTime.hour()).minute(toTime.minute()).second(0).millisecond(0)
    : null;
  const durationMs = startDt && endDt && endDt.isAfter(startDt)
    ? endDt.valueOf() - startDt.valueOf()
    : 0;
  const durationLabel = fmtMs(durationMs);

  function validate() {
    const errs = {};
    if (!date) errs.date = "Date is required";
    if (!fromTime) errs.fromTime = "Start time is required";
    if (!toTime) errs.toTime = "End time is required";
    if (startDt && endDt && !endDt.isAfter(startDt)) errs.toTime = "End time must be after start time";
    if (durationMs > 0 && durationMs < 60000) errs.toTime = "Minimum duration is 1 minute";
    if (durationMs > 8 * 60 * 60 * 1000) errs.toTime = "Maximum duration is 8 hours";
    if (endDt && endDt.isAfter(dayjs())) errs.toTime = "End time cannot be in the future";
    return errs;
  }

  async function handleSubmit() {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setLoading(true);
    try {
      await api.post("/api/v1/idle-requests", {
        date: date.format("YYYY-MM-DD"),
        startTime: startDt.toISOString(),
        endTime:   endDt.toISOString(),
        reason:    reason.trim() || null,
      });
      setSubmitted(true);
      toast.success("Idle time request submitted — pending admin approval");
      setTimeout(() => onClose(), 1800);
    } catch (err) {
      const msg = err.response?.data?.error?.message || "Failed to submit request";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  const antConfig = {
    algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
    token: {
      colorPrimary: "#3b82f6",
      borderRadius: 8,
      colorBgContainer: isDark ? "#1a1d2e" : "#ffffff",
      colorBgElevated: isDark ? "#1a1d2e" : "#ffffff",
      colorBorder: isDark ? "rgba(255,255,255,0.1)" : "rgba(15,23,42,0.15)",
      colorText: isDark ? "#ffffff" : "#0f172a",
      colorTextPlaceholder: isDark ? "rgba(255,255,255,0.35)" : "rgba(100,116,139,0.6)",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontSize: 13,
      controlHeight: 36,
    },
  };

  const labelStyle = {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: t.c.textSecondary,
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  };

  const errStyle = {
    fontSize: 11,
    color: t.status.negative,
    marginTop: 4,
  };

  return (
    <ConfigProvider theme={antConfig}>
      <Modal
        open={isOpen}
        onCancel={onClose}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: 8, background: t.status.warningSoft, color: t.status.warning }}>
              <Clock size={16} />
            </span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: t.c.text }}>Request idle time</div>
              <div style={{ fontSize: 12, color: t.c.textTertiary, fontWeight: 400 }}>Submit time for admin approval</div>
            </div>
          </div>
        }
        footer={
          submitted ? null : (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button onClick={onClose} disabled={loading}>Cancel</Button>
              <Button type="primary" onClick={handleSubmit} loading={loading}>Submit request</Button>
            </div>
          )
        }
        width={500}
        styles={{ body: { paddingTop: 8 } }}
        destroyOnClose
      >
        {submitted ? (
          <div style={{ textAlign: "center", padding: "28px 0" }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>✓</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: t.c.text, marginBottom: 6 }}>Request submitted</div>
            <div style={{ fontSize: 13, color: t.c.textTertiary }}>Your admin will review and approve your idle time.</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Date */}
            <div>
              <label style={labelStyle}>Date</label>
              <DatePicker
                value={date}
                onChange={(v) => { setDate(v); setErrors((p) => ({ ...p, date: null })); }}
                disabledDate={(d) => d && d.isAfter(dayjs(), "day")}
                format="DD MMM YYYY"
                picker="date"
                showTime={false}
                style={{ width: "100%" }}
                allowClear={false}
                popupStyle={{ width: "auto" }}
              />
              {errors.date && <div style={errStyle}>{errors.date}</div>}
            </div>

            {/* Time range */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <label style={labelStyle}>From</label>
                <TimePicker
                  value={fromTime}
                  onChange={(v) => { setFromTime(v); setErrors((p) => ({ ...p, fromTime: null, toTime: null })); }}
                  format="hh:mm A"
                  use12Hours
                  style={{ width: "100%" }}
                  allowClear={false}
                  placeholder="Start time"
                />
                {errors.fromTime && <div style={errStyle}>{errors.fromTime}</div>}
              </div>
              <div>
                <label style={labelStyle}>To</label>
                <TimePicker
                  value={toTime}
                  onChange={(v) => { setToTime(v); setErrors((p) => ({ ...p, toTime: null })); }}
                  format="hh:mm A"
                  use12Hours
                  style={{ width: "100%" }}
                  allowClear={false}
                  placeholder="End time"
                />
                {errors.toTime && <div style={errStyle}>{errors.toTime}</div>}
              </div>
            </div>

            {/* Live duration */}
            {durationLabel && (
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "6px 12px", borderRadius: 99,
                background: t.c.primarySoft, color: t.c.primary,
                fontSize: 13, fontWeight: 600, alignSelf: "flex-start",
              }}>
                <Clock size={13} /> {durationLabel}
              </div>
            )}

            {/* Reason */}
            {/* Ant renders showCount's "0 / 500" absolutely-positioned BELOW the
                textarea, so it reserves no height. As the last body child it
                escaped into the footer and collided with the buttons — the
                bottom padding here reserves room for it. */}
            <div style={{ paddingBottom: 18 }}>
              <label style={labelStyle}>
                Reason <span style={{ color: t.c.textTertiary, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span>
              </label>
              <Input.TextArea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="What were you doing during this time?"
                maxLength={500}
                rows={3}
                showCount
              />
            </div>

          </div>
        )}
      </Modal>
    </ConfigProvider>
  );
}
